/**
 * The GGUF header: magic, metadata, and the tensor index.
 *
 * ## Why this is written as a resumable parse rather than a read-then-parse
 *
 * safetensors states its header length in the first eight bytes, so two ranged
 * reads always suffice: read the length, read exactly that. GGUF states no such
 * thing. The index sits *after* the metadata, the metadata is dominated by the
 * tokenizer, and the tokenizer's arrays are strings — every entry has to be
 * walked to find where the array ends. There is no length field and no seek.
 *
 * Measured on real files (plan §4): a median of **10.46 MiB** of header to
 * reach roughly **36 KiB** of index, eight of nine sampled files above 5 MiB,
 * and no relationship to model size — a 4B and a 35B both sit at 10.46. So the
 * reader cannot know how much to ask for, and asking for "enough" would mean
 * asking for the largest header anyone has seen, on every file.
 *
 * Instead the parser runs against whatever bytes exist and raises
 * `NeedMoreBytes` naming the offset it could not reach. The caller fetches a
 * further range and retries. That keeps every read ranged and bounded, keeps the
 * *total* honest — the caller can report real bytes consumed as it goes, which
 * is what the status line shows — and means a small file costs one round trip
 * rather than a 16 MiB read.
 *
 * ## Sizes come from the layout, not only from the type table
 *
 * `ggml-types.ts` knows the block geometry of the published types, and 498 of
 * 851 tensors in one real public repo are ggml type **42**, which is in no
 * published enum. The offsets in the index still describe where every tensor
 * begins, so a tensor's extent is knowable whatever its type is. This module
 * therefore reports offsets and dimensions as read, and leaves the two ways of
 * deriving a byte count — from the type table, and from the layout — to
 * `inventory.ts`, which can compare them and say when they disagree.
 *
 * Pure: bytes in, structure out. No I/O, no `document`, no `window`.
 */

/** `GGUF`, little-endian. */
const MAGIC = 0x46554747;

/** Alignment of the tensor data region, when the metadata does not say. */
export const DEFAULT_ALIGNMENT = 32;

/**
 * Versions this parser will read.
 *
 * v3 is what the corpus is. v2 differs only in that lengths and counts are
 * already 64-bit, so the same code reads it; v1 used 32-bit counts and is not
 * accepted, because a v1 file would parse as garbage rather than fail.
 */
const SUPPORTED_VERSIONS: ReadonlySet<number> = new Set([2, 3]);

export type GgufValue = number | bigint | boolean | string | readonly GgufValue[];

export interface GgufTensorInfo {
  readonly name: string;
  /**
   * Logical element counts per axis, **as stored** — GGUF writes them
   * fastest-moving first, which is the reverse of how a shape is usually
   * printed. Reversed here so `shape` reads like every other shape in this
   * codebase; see `readTensorInfo`.
   */
  readonly shape: readonly number[];
  /** Product of `shape`. Elements, not bytes, for every type including unknown ones. */
  readonly numel: number;
  /** The raw ggml type id. Not resolved here — see `ggml-types.ts`. */
  readonly typeId: number;
  /** Byte offset within the tensor-data region, which begins at `dataStart`. */
  readonly offset: number;
}

export interface ParsedGgufHeader {
  readonly version: number;
  readonly tensorCount: number;
  readonly metadata: ReadonlyMap<string, GgufValue>;
  readonly tensors: readonly GgufTensorInfo[];
  /** Absolute file offset where tensor data begins, alignment applied. */
  readonly dataStart: number;
  /** Absolute file offset just past the tensor index, before alignment. */
  readonly indexEnd: number;
  readonly alignment: number;
}

/**
 * Raised when the parse ran past the bytes it was given.
 *
 * Carries the offset it needed so the caller can ask for a range that reaches
 * it rather than doubling blindly — a 5 MB token array is one string at a time,
 * and each retry would otherwise re-walk everything before it.
 */
export class NeedMoreBytes extends Error {
  constructor(readonly needed: number) {
    super(`GGUF header continues past the bytes read (need at least ${needed})`);
    this.name = "NeedMoreBytes";
  }
}

/** Not a GGUF file, or one this parser refuses to guess at. */
export class NotGguf extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotGguf";
  }
}

/** GGUF's metadata value types. */
enum ValueType {
  Uint8 = 0,
  Int8 = 1,
  Uint16 = 2,
  Int16 = 3,
  Uint32 = 4,
  Int32 = 5,
  Float32 = 6,
  Bool = 7,
  String = 8,
  Array = 9,
  Uint64 = 10,
  Int64 = 11,
  Float64 = 12,
}

/**
 * A cursor over the bytes read so far.
 *
 * Every read checks its own extent first, so overrunning raises `NeedMoreBytes`
 * with the offset required instead of reading zeroes out of an undersized
 * buffer — which would parse into a plausible, wrong header.
 */
class Cursor {
  private readonly view: DataView;
  offset = 0;

  constructor(private readonly bytes: ArrayBuffer) {
    this.view = new DataView(bytes);
  }

  private need(count: number): number {
    const at = this.offset;
    if (at + count > this.bytes.byteLength) throw new NeedMoreBytes(at + count);
    this.offset = at + count;
    return at;
  }

  u8(): number {
    return this.view.getUint8(this.need(1));
  }
  i8(): number {
    return this.view.getInt8(this.need(1));
  }
  u16(): number {
    return this.view.getUint16(this.need(2), true);
  }
  i16(): number {
    return this.view.getInt16(this.need(2), true);
  }
  u32(): number {
    return this.view.getUint32(this.need(4), true);
  }
  i32(): number {
    return this.view.getInt32(this.need(4), true);
  }
  f32(): number {
    return this.view.getFloat32(this.need(4), true);
  }
  f64(): number {
    return this.view.getFloat64(this.need(8), true);
  }
  bool(): boolean {
    return this.u8() !== 0;
  }

  /**
   * A 64-bit count, narrowed to a JS number.
   *
   * Refused rather than silently truncated above 2^53: every use here is a
   * length, a count or an offset, and a wrong one of those produces a confident
   * wrong inventory. A real file will not reach it; a corrupt or hostile one
   * might, and this is the cheapest place to stop.
   */
  u64(): number {
    const at = this.need(8);
    const value = this.view.getBigUint64(at, true);
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new NotGguf(`64-bit value ${value} exceeds safe integer range`);
    }
    return Number(value);
  }

  i64(): bigint {
    return this.view.getBigInt64(this.need(8), true);
  }

  /** A length-prefixed UTF-8 string. */
  str(): string {
    const length = this.u64();
    const at = this.need(length);
    return new TextDecoder().decode(new Uint8Array(this.bytes, at, length));
  }

  /**
   * Skip a string without decoding it.
   *
   * The tokenizer arrays are the bulk of every header — 5.1 MB of merges and
   * 4.9 MB of tokens on one measured file — and decoding a hundred thousand
   * strings we never look at is pure cost. The length prefix still has to be
   * read, because that is the only way to find the next one.
   */
  skipStr(): void {
    this.need(this.u64());
  }
}

/**
 * Metadata keys whose values are worth decoding.
 *
 * Everything else is skipped in place. This is not an optimisation detail: the
 * alternative is building a hundred-thousand-element array of strings for
 * `tokenizer.ggml.tokens` on every read, which costs more memory than the whole
 * rest of the page and is never displayed. The vocabulary's *size* is
 * interesting and is recorded from the array's element count without materialising it.
 */
const KEEP_PREFIXES = [
  "general.",
  "tokenizer.ggml.model",
  "tokenizer.ggml.bos_token_id",
  "tokenizer.ggml.eos_token_id",
  /*
   * Kept for its **length**, not its contents — `readValue` returns an array's
   * element count and discards the elements, so this costs a walk rather than
   * 5 MB of retained strings. Vocabulary size is the fact worth having, and for
   * a GGUF file it is the only place it is stated: there is no `config.json`
   * with a `vocab_size` beside it.
   *
   * Omitted at first, which the test for this caught: the walk happened, the
   * count was computed and then thrown away, so the one number the largest key
   * in the file can supply was the one number not taken from it.
   */
  "tokenizer.ggml.tokens",
] as const;

/** Architecture anchors live under the architecture's own prefix, e.g. `qwen35.`. */
function isInteresting(key: string): boolean {
  if (KEEP_PREFIXES.some((p) => key.startsWith(p))) return true;
  // `<arch>.block_count`, `<arch>.embedding_length`, `<arch>.attention.*`,
  // `<arch>.ssm.*` — the same facts `config.json` carries for safetensors, in
  // the same read. Matched on the suffix because the prefix is the model's own
  // architecture name and is not known until `general.architecture` is read.
  return /\.(block_count|embedding_length|feed_forward_length|rope\.|ssm\.|attention\.|expert_count|expert_used_count|vocab_size)/.test(
    key,
  );
}

/** Read a value of `type`, or skip it and return `undefined`. */
function readValue(c: Cursor, type: number, keep: boolean): GgufValue | undefined {
  /*
   * Every branch reads its value and then decides whether to keep it, rather
   * than reading conditionally. The cursor must advance either way — skipping a
   * value means consuming it, since the next key begins where this one ends —
   * so a branch that read only when `keep` was true would desynchronise the
   * whole parse on the first uninteresting key.
   */
  switch (type) {
    case ValueType.Uint8:
      return keepOr(c.u8(), keep);
    case ValueType.Int8:
      return keepOr(c.i8(), keep);
    case ValueType.Uint16:
      return keepOr(c.u16(), keep);
    case ValueType.Int16:
      return keepOr(c.i16(), keep);
    case ValueType.Uint32:
      return keepOr(c.u32(), keep);
    case ValueType.Int32:
      return keepOr(c.i32(), keep);
    case ValueType.Float32:
      return keepOr(c.f32(), keep);
    case ValueType.Bool:
      return keepOr(c.bool(), keep);
    case ValueType.Uint64:
      return keepOr(c.u64(), keep);
    case ValueType.Int64:
      return keepOr(c.i64(), keep);
    case ValueType.Float64:
      return keepOr(c.f64(), keep);
    case ValueType.String: {
      if (keep) return c.str();
      c.skipStr();
      return undefined;
    }
    case ValueType.Array: {
      const elementType = c.u32();
      const count = c.u64();
      /*
       * Arrays are walked, never bulk-skipped, and for string arrays there is no
       * alternative: the element width is not constant, so the only way past a
       * 100 000-token array is one length prefix at a time. For fixed-width
       * elements the walk is still cheap and keeps one code path.
       *
       * `keep` is deliberately not propagated to elements. A kept array reports
       * its *length* — which is the interesting fact, e.g. vocabulary size —
       * and discards the values, so `tokenizer.ggml.tokens` costs a walk and
       * not 5 MB of retained strings.
       */
      for (let i = 0; i < count; i++) readValue(c, elementType, false);
      return keep ? count : undefined;
    }
    default:
      throw new NotGguf(`Unknown GGUF metadata value type ${type}`);
  }
}

/** The value, or nothing — the read has already happened either way. */
function keepOr(value: GgufValue, keep: boolean): GgufValue | undefined {
  return keep ? value : undefined;
}

/**
 * Parse as much of the header as the given bytes allow.
 *
 * Throws `NeedMoreBytes` when it runs out, `NotGguf` when the bytes are not a
 * GGUF header this parser will read.
 */
export function parseGgufHeader(bytes: ArrayBuffer): ParsedGgufHeader {
  const c = new Cursor(bytes);

  if (bytes.byteLength < 4) throw new NeedMoreBytes(4);
  const magic = c.u32();
  if (magic !== MAGIC) {
    throw new NotGguf(
      `Not a GGUF file: magic 0x${magic.toString(16).padStart(8, "0")}`,
    );
  }

  const version = c.u32();
  if (!SUPPORTED_VERSIONS.has(version)) {
    // Named rather than attempted. A v1 file has 32-bit counts, so reading it
    // as v3 yields a plausible tensor count and nonsense offsets.
    throw new NotGguf(`GGUF version ${version} is not supported (need 2 or 3)`);
  }

  const tensorCount = c.u64();
  const kvCount = c.u64();

  const metadata = new Map<string, GgufValue>();
  for (let i = 0; i < kvCount; i++) {
    const key = c.str();
    const type = c.u32();
    const value = readValue(c, type, isInteresting(key));
    if (value !== undefined) metadata.set(key, value);
  }

  const tensors: GgufTensorInfo[] = [];
  for (let i = 0; i < tensorCount; i++) {
    tensors.push(readTensorInfo(c));
  }

  const indexEnd = c.offset;
  const declared = metadata.get("general.alignment");
  const alignment =
    typeof declared === "number" && declared > 0 ? declared : DEFAULT_ALIGNMENT;
  const dataStart = Math.ceil(indexEnd / alignment) * alignment;

  return { version, tensorCount, metadata, tensors, dataStart, indexEnd, alignment };
}

function readTensorInfo(c: Cursor): GgufTensorInfo {
  const name = c.str();
  const dimCount = c.u32();
  if (dimCount > 8) throw new NotGguf(`Tensor "${name}" claims ${dimCount} dimensions`);

  const dims: number[] = [];
  for (let i = 0; i < dimCount; i++) dims.push(c.u64());

  const typeId = c.u32();
  const offset = c.u64();

  /*
   * Reversed. GGUF writes `ne[]` fastest-moving-axis first, so a [4096, 2048]
   * matrix in the sense the rest of this codebase means is written
   * `[2048, 4096]`. Every consumer here — the role classifier's shape rules,
   * the low-rank detector, `e8m0-scaled.ts`'s last-axis arithmetic — reads the
   * last axis as the contiguous one, which is the opposite convention. Reversing
   * once, here, is the alternative to every one of them having to know.
   */
  const shape = dims.slice().reverse();
  const numel = shape.reduce((a, b) => a * b, 1);

  return { name, shape, numel, typeId, offset };
}
